/**
 * Etwas ins Bild holen — der eine Weg, auf dem ein **Sprung** an seinem Ziel
 * ankommt.
 *
 * Er steht hier und nicht zweimal in zwei Ansichten: Aus dem Befundvorrat führt
 * der Sprung in den Befundverlauf **und** in den Code, und beide sollen dieselbe
 * Bewegung machen. Zwei Fassungen wären zwei Gelegenheiten, verschieden weit zu
 * scrollen.
 *
 * **Mittig und weich.** Mittig, weil eine Stelle am oberen Rand aussieht wie der
 * Anfang der Liste und nicht wie ein Ziel; weich, weil ein Sprung ohne Bewegung
 * für den Leser ein Neuladen ist — er sieht nicht, dass er sich bewegt hat, und
 * sucht dann doch.
 *
 * **Und ausdrücklich nicht `scrollIntoView`.** Der Browser scrollt damit *jeden*
 * Vorfahren, der etwas verschieben kann — auch den, der `overflow: hidden` trägt.
 * Die Hülle dieser Anwendung ist genau so einer: Sie liegt fest im Fenster und
 * scrollt nie. Setzt ihr jemand den Scrollstand, ist die Oberfläche verschoben,
 * der Kopf nicht mehr erreichbar und unten steht ein leeres Band — und
 * zurückscrollen kann der Anwender nicht, weil die Fläche für ihn gar nicht
 * scrollbar ist. Deshalb wird hier selbst gerechnet und nur angefasst, was
 * tatsächlich scrollt.
 */

/**
 * Die Fläche, in der ein Sprung landet, samt aller Flächen darüber — **von innen
 * nach außen**.
 *
 * Von innen nach außen, weil jede innere Bewegung die Lage des Ziels im Fenster
 * verändert: Erst wenn der Code in seinem Kasten an der richtigen Stelle steht,
 * lässt sich rechnen, wie weit die Seite darunter noch muss.
 */
function scrollingAncestors(element: Element): HTMLElement[] {
  const found: HTMLElement[] = [];

  for (let node = element.parentElement; node !== null; node = node.parentElement) {
    const overflow = getComputedStyle(node).overflowY;

    // `auto` und `scroll` scrollt der Anwender selbst; `hidden` und `visible`
    // nicht — und was er nicht zurückscrollen kann, wird auch nicht verschoben.
    if (
      (overflow === "auto" || overflow === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      found.push(node);
    }
  }

  return found;
}

/**
 * Wie weit ein Sprung bewegt.
 *
 * `start` ist der **Sprung an einen Block**: Sein Anfang steht oben, und man liest
 * ihn von dort nach unten. Für alles, was höher ist als das Fenster, ist das die
 * einzige brauchbare Landung — mittig gestellt beginnt ein langer Block oberhalb
 * des Bildrands, und man sieht seine Mitte und hält sie für das Ende.
 *
 * `center` ist der Sprung an eine **einzelne Zeile** in einer Fläche, die größer ist
 * als sie: Am oberen Rand sähe sie aus wie der Anfang der Liste und nicht wie das
 * Gesuchte.
 *
 * `nearest` ist die **Rückkehr an eine Zeile**, die man vielleicht noch sieht: Sie
 * bewegt nur, was nötig ist, und eine ohnehin sichtbare Zeile verrückt die Liste
 * gar nicht. Mittig wäre hier falsch — die Liste sähe nach jeder Rückkehr anders
 * aus als beim Verlassen.
 */
export type Reach = "start" | "center" | "nearest";

/**
 * Wie sich die **äußerste** Fläche bewegt — die, die der Anwender als „die Seite"
 * sieht.
 *
 * `smooth` ist der Regelfall, und sein Grund steht oben: Ein Sprung ohne Bewegung
 * ist für den Leser ein Neuladen — er sieht nicht, dass er sich bewegt hat, und
 * sucht dann doch.
 *
 * `instant` ist der Gegenfall, und er hat einen eigenen Grund: Wer eine Stelle
 * bewertet hat und dabei zur nächsten unbewerteten weitergereicht wird (#187),
 * hat diesen Sprung nicht ausgelöst, um ihm zuzusehen. Er arbeitet eine Revision
 * ab, und die Bewegung steht zwischen zwei Handgriffen — dort ist sie keine
 * Auskunft, sondern Wartezeit, und beim vierten Mal eine Zumutung.
 *
 * **Das kehrt die Regel nicht um.** Jeder Sprung, den der Anwender selbst
 * auslöst, bleibt weich; `instant` gilt allein dort, wo das System ihn
 * weiterschiebt.
 */
export type Motion = "smooth" | "instant";

/**
 * Der Abstand über einem Block, der an den Anfang springt.
 *
 * Ohne ihn klebt die erste Zeile am Rand und sieht abgeschnitten aus; mit ihm ist
 * zu sehen, dass darüber etwas endet. Eine Haarlinie mehr wäre schon eine
 * Gestaltungsentscheidung — deshalb genau die Höhe eines Zeilenabstands.
 */
const GAP = 12;

/** Das Ziel in dieser Fläche ins Bild stellen. */
function bring(
  element: Element,
  container: HTMLElement,
  reach: Reach,
  behavior: ScrollBehavior,
): void {
  const box = element.getBoundingClientRect();
  const frame = container.getBoundingClientRect();
  const above = box.top - frame.top;
  const below = box.bottom - frame.bottom;

  if (reach === "nearest") {
    // Ganz drin: Es gibt nichts zu bewegen.
    if (above >= 0 && below <= 0) {
      return;
    }

    // Sonst so weit, dass es hineinpasst — nach oben oder nach unten, nie weiter.
    scroll(container, container.scrollTop + (above < 0 ? above : below), behavior);

    return;
  }

  if (reach === "start") {
    scroll(container, container.scrollTop + above - GAP, behavior);

    return;
  }

  scroll(container, container.scrollTop + above - (frame.height - box.height) / 2, behavior);
}

function scroll(container: HTMLElement, top: number, behavior: ScrollBehavior): void {
  // Nicht über den Anfang hinaus: Ein negativer Scrollstand ist keiner, und ein
  // Ziel in den ersten Zeilen soll oben stehen und nicht in der Mitte kleben.
  const wanted = Math.max(0, top);

  // `scrollTo` gibt es in jsdom nicht, weil jsdom kein Layout rechnet. Die
  // Abfrage steht hier und nicht als Attrappe in der Testreihe: Eine Ansicht,
  // die in der Prüfumgebung stirbt, ist kein Beleg für die im Browser.
  if (typeof container.scrollTo === "function") {
    container.scrollTo({ top: wanted, behavior });

    return;
  }

  container.scrollTop = wanted;
}

/**
 * Das Ziel in **genau dieser** Fläche ins Bild holen und sonst nirgends.
 *
 * Der Unterschied zu `bringIntoView` ist der Grund, aus dem es beides gibt: Eine
 * markierte Codezeile soll in ihrem Kasten mittig stehen — die **Seite** darunter
 * hat damit nichts zu tun. Zog sie mit, landete man bei einem hohen Block in dessen
 * Mitte, weil die Zeile dort steht, und hielt sie für das Ende. Wohin die Seite
 * springt, entscheidet der Block und nicht die Zeile.
 */
export function bringWithin(
  container: HTMLElement | null | undefined,
  element: Element | null | undefined,
  reach: Reach = "center",
): void {
  if (container === null || container === undefined || element === null || element === undefined) {
    return;
  }

  bring(element, container, reach, "auto");
}

export function bringIntoView(
  element: Element | null | undefined,
  reach: Reach = "center",
  motion: Motion = "smooth",
): void {
  if (element === null || element === undefined) {
    return;
  }

  const containers = scrollingAncestors(element);

  containers.forEach((container, at) => {
    // Die inneren Flächen springen sofort, damit die nächste Rechnung auf dem
    // fertigen Stand aufsetzt; die äußerste — die, die der Anwender als „die
    // Seite" sieht — bewegt sich weich. Weich in allen wäre nicht nur unruhig,
    // sondern falsch: Eine Bewegung, die erst später ankommt, hinterlässt der
    // nächsten Rechnung eine Lage, die es noch nicht gibt.
    //
    // Eine Rückkehr bewegt gar nicht weich: Sie soll die Liste hinstellen, wie sie
    // war, und nicht vorführen, dass sie sich bewegt hat. Dasselbe gilt für den
    // Sprung, um den niemand gebeten hat — siehe `Motion`.
    const behavior: ScrollBehavior =
      reach === "nearest" || motion === "instant" || at < containers.length - 1
        ? "auto"
        : "smooth";

    bring(element, container, reach, behavior);
  });
}
